// Cómo genera el modo Redes — ADR-2026-10-01-modo-redes.
//
// No hay motor nuevo: Imagen es `generateFreeformImage` tal cual lo llama Prompt libre, y
// Video es `generateVideoFromPrompt` tal cual lo llama el Creador. Lo que cambia es a dónde
// va el resultado: una TANDA del lienzo del Agente (`agregarTanda`), que espera ids de
// `imagenes_generadas` ya con archivo. Por eso cada promesa termina con el preload, como
// hace `generarEnDiseno`.
//
// Las dos funciones devuelven lo que `agregarTanda` necesita además de la promesa (total,
// proporción, si es video, el `executionId` con el que se reanuda tras recargar) y, para el
// video, cuánto se estima que tarda, que es lo que pacea la barra del recuadro.

import type { ChatAttachment } from '../../agentAttachments';
import { BUCKET_ADJUNTOS_DEL_CHAT, signReferenceImage } from '../../agentAttachments';
import { buildAnimarVideoBody, generateVideoFromImage, type AnimarSource } from '../../../../lib/videoFromImage';
import { getProductFullImageUrl } from '../../productsApi';
import { recordPromptHistory } from '../../promptHistory';
import { MAX_REFERENCIAS_VIDEO } from '../../referenciasDeGeneracion';
import { ratioDelFormatoElegido, resolverReferencias } from '../promptLibre/promptLibre';
import { formatSlugToAspect } from '../../agentGenerate';
import { camposDeFormatoPersonalizado } from '../../../../utils/formatoPersonalizado';
import { escribirPromptDeVideo } from '../../../../lib/videoCreator';
import { idsDeVideo } from '../creadorVideo/creadorVideo';
import { estimarDuracionVideoMs } from '../../../../utils/estimacionVideo';
import { preloadGeneratedImages } from '../../../../utils/preloadGeneratedImages';
import { preloadVideosDeTanda } from '../../../../utils/preloadVideosDeTanda';
import { duracionDeAnimar, esAnimar, esAutomatico, type AjustesDeRedes, type DeclaradoDeImagen } from './redes';
import { conMenciones } from '../../mencionesDeAdjuntos';
import { escribirPromptDeImagen } from '../../../../lib/escritorDeImagen';
import { deImagenANombre, deNombreAImagen } from './mencionesDeVideo';
import { armarReceta } from './recetaDeRedes';
import type { ProveedorDeImagen } from '../../../../lib/proveedoresDeImagen';
import { postGenerateAd } from '../../../../lib/generateAdRequest';
import { GENERATE_AD_V3_URL } from '../../../../config';
import { awaitImageGeneration } from '../../imageGenerationRecovery';
import { PIN_NO_ES_CUADRO } from '../../../../lib/pinterestConexionLogica';

export interface PedidoDeRedes {
  userId: string;
  companyId: string;
  prompt: string;
  adjuntos: ChatAttachment[];
  ajustes: AjustesDeRedes;
  /** La foto de presentación de un modelo guardado, por id; `null` si no tiene. */
  modeloUrl: (modelId: string) => string | null;
  /** El prompt ya es el del escritor ("Mejorar prompt") y quedó igual o casi: el Creador no
   *  lo vuelve a escribir (ADR-2026-10-04-mejorar-prompt-en-redes). En imagen además hace
   *  falta `declarado`: sin él no hay con qué elegir el motor, y se vuelve a escribir. */
  promptEscrito?: boolean;
  /** Imagen: lo que "Mejorar prompt" declaró (tarea, piel, fondo blanco, etiquetas). Viaja
   *  tal cual con el prompt ya escrito, así no se decide dos veces
   *  (ADR-2026-10-04-el-escritor-de-imagen-usa-las-reglas-del-agente). */
  declarado?: DeclaradoDeImagen;
  /** Sólo cuentas dev: el motor de imagen forzado desde la caja
   *  (ADR-2026-10-05-forzar-el-modelo-en-redes). Sólo imagen. */
  proveedorForzado?: ProveedorDeImagen | null;
}

/** Lo que el lienzo necesita para colgar la tanda. */
export interface TandaPedida {
  executionId: string;
  total: number;
  ratio: number;
  esVideo: boolean;
  duracionEstimadaMs?: number;
  /** Ids de `imagenes_generadas`, ya con archivo. */
  resultado: Promise<string[]>;
}

const avisarCreditos = () => {
  // El backend ya descontó los créditos: el pill del shell escucha este evento.
  try {
    window.dispatchEvent(new Event('atlas:credits-updated'));
  } catch {
    /* noop */
  }
};

/** Preload de las filas antes de resolver, como la columna de resultados y el lienzo. */
async function conPreloadDeImagenes(ids: string[], companyId: string): Promise<string[]> {
  try {
    const pre = await preloadGeneratedImages(ids, companyId);
    if (pre.length > 0) return pre.map((x) => String(x.id));
  } catch {
    // Sin preload los ids igual sirven: la miniatura se pide por su cuenta.
  }
  return ids;
}

async function conPreloadDeVideos(ids: string[], companyId: string): Promise<string[]> {
  if (ids.length === 0) return ids;
  try {
    const pre = await preloadVideosDeTanda(ids, companyId);
    if (pre.length > 0) return pre.map((x) => String(x.id));
  } catch {
    /* ídem */
  }
  return ids;
}

const depsDeReferencias = (p: PedidoDeRedes) => ({
  productoUrl: (id: string) => getProductFullImageUrl(p.companyId, id),
  modeloUrl: (id: string) => p.modeloUrl(id),
  adjuntoUrl: (path: string, bucket?: string) => signReferenceImage(path, bucket),
});

// `conMenciones` vive en `mencionesDeAdjuntos` (la comparte la barra de Editar del visor).

/**
 * Una tanda de IMÁGENES: lo mismo que `PromptLibreSheet.handleGenerar`, más el
 * metaprompting del agente. Si el prompt no viene de "Mejorar prompt", el backend lo redacta
 * y elige el motor como paso del job (`escribirPrompt`); si viene, viaja con lo que esa
 * mejora ya declaró y no se vuelve a escribir.
 */
export function pedirImagenEnRedes(p: PedidoDeRedes): TandaPedida {
  const executionId = crypto.randomUUID();
  const promptFinal = p.prompt.trim();
  const { formatSlug, calidad, cantidad } = p.ajustes.imagen;
  // Recientes: el MISMO historial que Prompt libre, así lo escrito en un modo aparece en
  // el otro. Fire-and-forget.
  recordPromptHistory(p.companyId, 'prompt_libre', promptFinal);

  const resultado = (async () => {
    const yaEscrito = p.promptEscrito === true && !!p.declarado;
    const referenceImages = conMenciones(
      await resolverReferencias(p.adjuntos, depsDeReferencias(p)),
      p.adjuntos,
      yaEscrito ? p.declarado?.etiquetas : [],
    );
    const { generateFreeformImage } = await import('../../agentGenerate');
    const { imageIds } = await generateFreeformImage({
      userId: p.userId,
      prompt: promptFinal,
      formatSlug,
      cantidad,
      companyId: p.companyId,
      executionId,
      imageSize: calidad,
      modoUtilizado: 'redes',
      ...(referenceImages.length > 0 ? { referenceImages } : {}),
      ...(yaEscrito && p.declarado
        ? {
            tarea: p.declarado.tarea,
            ...(p.declarado.edicion === 'fusionar' ? { edicion: 'fusionar' as const } : {}),
            ...(p.declarado.pielExpuesta ? { pielExpuesta: true } : {}),
            ...(p.declarado.fondoBlanco ? { fondoBlanco: true, luzFondoBlanco: p.declarado.luzFondoBlanco } : {}),
          }
        : { escribirPrompt: true }),
      // Lo que había en la caja, para "Rehacer" desde la cuadrícula.
      receta: armarReceta(p.prompt, p.adjuntos, p.ajustes),
      ...(p.proveedorForzado ? { providerOverride: p.proveedorForzado } : {}),
    });
    avisarCreditos();
    return conPreloadDeImagenes(imageIds.map(String), p.companyId);
  })();

  return {
    executionId,
    total: cantidad,
    ratio: ratioDelFormatoElegido(formatSlug),
    esVideo: false,
    resultado,
  };
}

/**
 * Una tanda de IMÁGENES en AUTOMÁTICO (ADR-2026-10-09-automatico-adentro-de-crear): el MISMO
 * pedido que arma el modo Automático (`InterfazV2`, `isDesignAuto`) —`modo: 'rapido'`, el
 * director de arte del backend escribe una escena distinta por imagen—, con los productos de
 * la caja. El texto, si hay, viaja como indicación extra; modelo y referencias no viajan (el
 * modo no los usa). Mismo costo por imagen que una tanda normal.
 */
export function pedirAutomaticoEnRedes(p: PedidoDeRedes): TandaPedida {
  const executionId = crypto.randomUUID();
  const { formatSlug, calidad, cantidad } = p.ajustes.imagen;
  const variantes = p.adjuntos.flatMap((a) => (a.kind === 'product' ? [a.productId] : []));
  const indicacion = p.prompt.trim();
  if (indicacion) recordPromptHistory(p.companyId, 'prompt_libre', indicacion);

  const resultado = (async () => {
    // Mismo guard que las generaciones del agente: sin los formatos cargados, el pedido
    // saldría sin la copia del formato personalizado (revisión de Greptile al PR #197).
    const { asegurarFormatoDelPedido } = await import('../../agentGenerate');
    await asegurarFormatoDelPedido(formatSlug);
    const { imageIds } = await awaitImageGeneration({
      fetchPromise: postGenerateAd(
        {
          user_id: p.userId,
          variante_id: variantes,
          mode: variantes.length > 1 ? 'multiple' : 'single',
          modo: 'rapido',
          modo_utilizado: 'imagen_automatica',
          cantidad_imagenes: cantidad,
          Format: formatSlugToAspect(formatSlug),
          ...camposDeFormatoPersonalizado(formatSlug),
          image_size: calidad,
          composicion: 'producto',
          background_mode: 'ia',
          new_ui: true,
          modo_pro: true,
          execution_id: executionId,
          ...(indicacion ? { instruccion_adicional: indicacion } : {}),
        },
        { url: GENERATE_AD_V3_URL },
      ),
      executionId,
      companyId: p.companyId,
      expectedCount: cantidad,
    });
    avisarCreditos();
    return conPreloadDeImagenes(imageIds.map(String), p.companyId);
  })();

  return {
    executionId,
    total: cantidad,
    ratio: ratioDelFormatoElegido(formatSlug),
    esVideo: false,
    resultado,
  };
}

/** Una tanda de VIDEO: lo mismo que `CreadorVideoSheet.handleGenerar`, en una función. */
export function pedirVideoEnRedes(p: PedidoDeRedes): TandaPedida {
  const executionId = crypto.randomUUID();
  const promptFinal = p.prompt.trim();
  const { formatSlug, ajustes, tipoVideo } = p.ajustes.video;
  const variantIds = p.adjuntos.flatMap((a) => (a.kind === 'product' ? [a.productId] : []));
  const duracionEstimadaMs = estimarDuracionVideoMs({
    quality: ajustes.quality,
    resolution: ajustes.resolution,
    durationSeconds: ajustes.durationSeconds,
  });
  if (promptFinal) recordPromptHistory(p.companyId, 'video', promptFinal);

  const resultado = (async () => {
    if (tienePines(p)) throw new Error(PIN_NO_ES_CUADRO);
    const referenceImages = await resolverReferencias(p.adjuntos, depsDeReferencias(p), MAX_REFERENCIAS_VIDEO);
    // Un VIDEO adjunto (una creación sumada como referencia) viaja aparte, como video de
    // referencia en modo `referencia`: el video nuevo toma de él el estilo, el ritmo y la
    // cámara, y el contenido sale del prompt. Uno solo, que es lo que acepta el proveedor
    // (ADR-2026-10-05-acciones-de-video-en-la-cuadricula-de-redes). Si no se puede firmar,
    // el pedido sale sin él.
    const video = p.adjuntos.find((a) => a.kind === 'video');
    const urlDelVideo = video && video.kind === 'video' ? await signReferenceImage(video.storagePath, video.bucket).catch(() => null) : null;
    const { generateVideoFromPrompt } = await import('../../agentGenerate');
    const { videoId } = await generateVideoFromPrompt({
      userId: p.userId,
      // En la caja las referencias se nombran (`@Remera oversize`); Seedance las conoce
      // por posición (`@Image1`). Se traduce acá, contra las MISMAS referencias que viajan
      // (ADR-2026-10-04-las-menciones-de-video-se-ven-por-nombre). El historial de
      // prompts guarda lo que el usuario vio.
      prompt: deNombreAImagen(promptFinal, referenceImages, p.adjuntos),
      formatSlug,
      durationSeconds: ajustes.durationSeconds,
      quality: ajustes.quality,
      resolution: ajustes.resolution,
      companyId: p.companyId,
      executionId,
      modoUtilizado: 'redes',
      tipoVideo,
      ...(p.promptEscrito ? { promptEscrito: true } : {}),
      receta: armarReceta(p.prompt, p.adjuntos, p.ajustes),
      ...(variantIds.length > 0 ? { variantIds } : {}),
      ...(referenceImages.length > 0 ? { referenceImages } : {}),
      ...(urlDelVideo ? { referenceVideos: [{ url: urlDelVideo, modo: 'referencia' as const }] } : {}),
    });
    avisarCreditos();
    return conPreloadDeVideos(idsDeVideo(videoId), p.companyId);
  })();

  return {
    executionId,
    total: 1,
    ratio: ratioDelFormatoElegido(formatSlug),
    esVideo: true,
    duracionEstimadaMs,
    resultado,
  };
}

/** De dónde sale la foto a animar: la imagen de "Añadir referencia" (subida o creación). */
/** El video no toma pines (el backend exige nuestro Storage): si quedaron de cuando era imagen,
 *  se avisa en vez de mandarlos y rebotar (ADR-2026-10-26-pinterest-abrir-a-todos). */
function tienePines(p: PedidoDeRedes): boolean {
  return p.adjuntos.some((a) => a.kind === 'referencia' && a.tipo === 'pinterest');
}

export function fuenteDeAnimar(adjuntos: PedidoDeRedes['adjuntos']): AnimarSource | null {
  const img = adjuntos.find((a) => a.kind === 'image');
  if (!img || img.kind !== 'image') return null;
  return { tipo: 'subida', bucket: img.bucket ?? BUCKET_ADJUNTOS_DEL_CHAT, path: img.storagePath };
}

/**
 * Una tanda de ANIMAR IMAGEN (ADR-2026-10-09-modos-adentro-de-crear): el MISMO pedido que el
 * modo Animar imagen (`AnimarImagenMode`, `buildAnimarVideoBody` + `generateVideoFromImage`),
 * con la foto de "Añadir referencia" como primer cuadro y el texto de la caja como movimiento.
 * Productos y modelos no viajan. 1 crédito por segundo, de 4 a 15.
 */
export function pedirAnimarEnRedes(p: PedidoDeRedes): TandaPedida {
  const executionId = crypto.randomUUID();
  const fuente = fuenteDeAnimar(p.adjuntos);
  const duracion = duracionDeAnimar(p.ajustes.video.ajustes.durationSeconds);
  const promptFinal = p.prompt.trim();
  if (promptFinal) recordPromptHistory(p.companyId, 'video', promptFinal);

  const resultado = (async () => {
    // Un pin no se anima ni se ignora en silencio (se cobraría un video sin él): se avisa.
    if (tienePines(p)) throw new Error(PIN_NO_ES_CUADRO);
    if (!fuente) throw new Error('Elegí la foto a animar.');
    const body = buildAnimarVideoBody(fuente, {
      userId: p.userId,
      prompt: promptFinal,
      duracion,
      multiplesEscenas: false,
    });
    // Como el modo en el panel: el lienzo espera el archivo del video.
    const videoId = await generateVideoFromImage(body, { companyId: p.companyId });
    avisarCreditos();
    return [String(videoId)];
  })();

  return {
    executionId,
    total: 1,
    // Animar sale siempre en 9:16 (`buildAnimarVideoBody`).
    ratio: 9 / 16,
    esVideo: true,
    duracionEstimadaMs: estimarDuracionVideoMs({ quality: 'basica', resolution: '720p', durationSeconds: duracion }),
    resultado,
  };
}

export const pedirEnRedes = (p: PedidoDeRedes): TandaPedida =>
  esAnimar(p.ajustes)
    ? pedirAnimarEnRedes(p)
    : p.ajustes.medio === 'video'
      ? pedirVideoEnRedes(p)
      : esAutomatico(p.ajustes)
        ? pedirAutomaticoEnRedes(p)
        : pedirImagenEnRedes(p);

/**
 * "Mejorar prompt" en VIDEO: el escritor del Creador, suelto, con lo mismo que después va
 * a generar (texto, tipo, duración, formato, referencias firmadas y productos). Devuelve el
 * prompt para ponerlo en la caja (ADR-2026-10-04-mejorar-prompt-en-redes). El body se arma
 * como el de `generateVideoFromPrompt` para que la leyenda `@ImageN` numere igual.
 */
async function escribirVideoEnRedes(p: PedidoDeRedes): Promise<string> {
  if (tienePines(p)) throw new Error(PIN_NO_ES_CUADRO);
  const { formatSlug, ajustes, tipoVideo } = p.ajustes.video;
  const variantIds = p.adjuntos.flatMap((a) => (a.kind === 'product' ? [a.productId] : []));
  const referencias = await resolverReferencias(p.adjuntos, depsDeReferencias(p), MAX_REFERENCIAS_VIDEO);
  const r = await escribirPromptDeVideo({
    // Las menciones del usuario van como `@ImageN`, que es como el escritor ve la leyenda.
    prompt: deNombreAImagen(p.prompt.trim(), referencias, p.adjuntos),
    tipo_video: tipoVideo,
    duration: ajustes.durationSeconds,
    aspect_ratio: formatSlugToAspect(formatSlug),
    ...(referencias.length > 0
      ? {
          reference_images: referencias.map((r) => ({
            ...(r.url ? { url: r.url } : {}),
            tipo: r.tipo,
            ...(r.tipo === 'producto' && r.productId ? { product_id: r.productId } : {}),
            ...(r.tipo === 'producto' && r.vista ? { vista: r.vista } : {}),
            ...(r.etiqueta ? { etiqueta: r.etiqueta } : {}),
          })),
        }
      : {}),
    ...(variantIds.length > 0 ? { variante_ids: variantIds } : {}),
  });
  // Y lo que escribió vuelve a la caja con nombres: `@Image1` no le dice nada al usuario.
  return deImagenANombre(r.prompt, referencias, p.adjuntos);
}

/**
 * "Mejorar prompt" en IMAGEN: el escritor de imagen del backend, con las reglas del agente.
 * Devuelve el prompt (las fotos siguen nombradas `@Nombre`) y lo que declaró, con las
 * etiquetas de las fotos subidas atadas al id de su adjunto.
 */
async function escribirImagenEnRedes(p: PedidoDeRedes): Promise<{ prompt: string; declarado: DeclaradoDeImagen }> {
  const referencias = conMenciones(await resolverReferencias(p.adjuntos, depsDeReferencias(p)), p.adjuntos);
  const r = await escribirPromptDeImagen({
    prompt: p.prompt.trim(),
    Format: formatSlugToAspect(p.ajustes.imagen.formatSlug),
    ...(referencias.length > 0
      ? {
          reference_images: referencias.map((x) => ({
            ...(x.url ? { url: x.url } : {}),
            tipo: x.tipo,
            ...(x.productId ? { product_id: x.productId } : {}),
            ...(x.etiqueta ? { etiqueta: x.etiqueta } : {}),
            ...(x.mencion ? { mencion: x.mencion } : {}),
          })),
        }
      : {}),
  });
  const porMencion = new Map(referencias.flatMap((x) => (x.mencion && x.adjuntoId ? [[x.mencion.toLowerCase(), x.adjuntoId] as const] : [])));
  return {
    prompt: r.prompt,
    declarado: {
      tarea: r.tarea,
      edicion: r.edicion,
      pielExpuesta: r.piel_expuesta,
      fondoBlanco: r.fondo_blanco,
      luzFondoBlanco: r.luz_fondo_blanco,
      etiquetas: r.etiquetas.flatMap((e) => {
        const adjuntoId = porMencion.get(e.mencion.replace(/^@/, '').toLowerCase());
        return adjuntoId ? [{ adjuntoId, etiqueta: e.etiqueta }] : [];
      }),
    },
  };
}

/** "Mejorar prompt": el escritor del medio activo. En imagen, además, lo que declaró. */
export async function escribirPromptEnRedes(p: PedidoDeRedes): Promise<{ prompt: string; declarado?: DeclaradoDeImagen }> {
  return p.ajustes.medio === 'video' ? { prompt: await escribirVideoEnRedes(p) } : escribirImagenEnRedes(p);
}
